/**
 * ProjectSummaryCard — the unified hero for a project page.
 *
 * One card that tells the full story:
 *   Header:  project identity (name, tech, capacity, BA, status)
 *   Middle:  generation + fossil displacement + CO2e avoided
 *   Bottom:  the three LCA endpoint metrics (DALYs, PDFs, USD)
 *
 * Replaces the old separate ImpactSummaryCard + DisplacementHero.
 */

import { useState, useMemo } from 'react';
import { FiEdit2, FiInfo, FiPlay, FiTrash2, FiX, FiChevronLeft } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { ImpactTotals } from '../../impact/domain/types';
import { LifetimeProjection } from '../../lifetime/domain/types';
import { formatImpact, toSigFigs, formatScientific, formatCompactValue } from '../../../utils/format';
import { useImpactMode } from '../../impact/context/ImpactModeContext';
import { useRangeMode } from '../../impact/context/RangeModeContext';
import { RangeWhisker } from '../../impact/components/RangeVisuals';
import { isRangeReady, metricRange, type ScenariosBlock } from '../../impact/domain/scenarios';
import { useGridBaseline } from '../../baseline/hooks/useBaseline';
import { ENDPOINT_DEFINITIONS, TECH_LABELS, LIFECYCLE_LABELS, getFuelLabel } from '../domain/definitions';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ProjectSummaryCardProps {
    projectName: string;
    techType: string;
    capacity: number;
    location: string;
    analysisYear: number;
    impactTotals: ImpactTotals | null;
    opsTotals?: ImpactTotals | null;
    lifetimeProjection: LifetimeProjection | null;
    onEditDetails?: () => void;
    onRunModel?: () => void;
    onRename?: () => void;
    onDelete?: () => void;
    canRun?: boolean;
    lensToggle?: React.ReactNode;
    baCode?: string;
    generationMwh?: number;
    isExisting?: boolean;
    generationData?: Record<string, { Difference?: number }>;
    avoidedCo2eTonnes?: number;
    /** Back navigation — { to: path, label: display text } */
    backTo?: { to: string; label: string };
    /** Report buttons rendered into the action row */
    reportButtons?: React.ReactNode;
    /** Optional Central ⇄ Range sensitivity bracket for this run. */
    scenarios?: ScenariosBlock;
}

// ---------------------------------------------------------------------------
// Fuel bar colors (consistent with GenerationChangeChart)
// ---------------------------------------------------------------------------

const FUEL_DOT_COLORS: Record<string, string> = {
    COL: '#6B7280', Coal: '#6B7280',
    NG: '#F97316', 'Natural Gas': '#F97316',
    NUC: '#A855F7', Nuclear: '#A855F7',
    OIL: '#92400E', Oil: '#92400E', Petroleum: '#92400E',
    WAT: '#3B82F6', Hydro: '#3B82F6', Water: '#3B82F6',
    SUN: '#FBBF24', Solar: '#FBBF24',
    WND: '#14B8A6', Wind: '#14B8A6',
    OTH: '#D1D5DB', Other: '#D1D5DB',
    GEO: '#14B8A6', BIO: '#84CC16',
};
function getFuelDotColor(fuel: string): string {
    if (FUEL_DOT_COLORS[fuel]) return FUEL_DOT_COLORS[fuel];
    const key = Object.keys(FUEL_DOT_COLORS).find(
        (k) => fuel.toLowerCase().includes(k.toLowerCase())
    );
    return key ? FUEL_DOT_COLORS[key] : '#9CA3AF';
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function InfoTooltip({ category }: { category: string }) {
    const [open, setOpen] = useState(false);
    const info = ENDPOINT_DEFINITIONS[category];
    if (!info) return null;

    return (
        <span className="relative inline-block">
            <button
                onClick={() => setOpen(!open)}
                className="text-text-muted hover:text-text-secondary transition-colors ml-1 align-middle"
                aria-label={`About ${info.title}`}
            >
                <FiInfo size={12} />
            </button>
            {open && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
                    <div className="absolute left-0 top-full mt-1 z-50 w-72 rounded-lg bg-surface-600 border border-border shadow-lg p-3">
                        <div className="flex items-start justify-between mb-1">
                            <span className="text-xs font-semibold text-text-primary">{info.title}</span>
                            <button onClick={() => setOpen(false)} className="text-text-muted hover:text-text-secondary">
                                <FiX size={12} />
                            </button>
                        </div>
                        <p className="text-[11px] leading-relaxed text-text-secondary">{info.description}</p>
                    </div>
                </>
            )}
        </span>
    );
}

function BaselineContext({ annualValue, generationMwh, gridPerMwh, gridGenMwh, metric }: {
    annualValue: number;
    generationMwh?: number;
    gridPerMwh: number | null | undefined;
    gridGenMwh: number | null | undefined;
    metric: 'dalys' | 'pdfs' | 'usd';
}) {
    const perMwh = generationMwh ? annualValue / generationMwh : null;
    const gridAnnual = gridPerMwh != null && gridGenMwh ? gridPerMwh * gridGenMwh : null;
    const unitLabel = metric === 'usd' ? 'USD' : metric === 'dalys' ? 'DALYs' : 'PDFs';

    return (
        <div className="mt-1.5 space-y-0.5">
            {perMwh != null && (
                <p className="text-[11px] text-text-muted">
                    {formatScientific(Math.abs(perMwh))} /MWh
                </p>
            )}
            {gridAnnual != null && (
                <p className="text-[11px] text-red-400/60">
                    Grid: {metric === 'usd'
                        ? formatImpact(Math.abs(gridAnnual), 'usd')
                        : `${formatCompactValue(Math.abs(gridAnnual))} ${unitLabel}`}/yr
                    {gridPerMwh != null && (
                        <span className="text-text-muted"> ({formatScientific(Math.abs(gridPerMwh))})</span>
                    )}
                </p>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function ProjectSummaryCard({
    projectName,
    techType,
    capacity,
    analysisYear,
    impactTotals,
    opsTotals,
    lifetimeProjection,
    onEditDetails,
    onRunModel,
    onRename,
    onDelete,
    canRun,
    lensToggle,
    baCode,
    generationMwh,
    isExisting,
    generationData,
    avoidedCo2eTonnes,
    backTo,
    reportButtons,
    scenarios,
}: ProjectSummaryCardProps) {
    const { mode } = useImpactMode();
    const { mode: rangeMode } = useRangeMode();
    const lens = mode === 'ops' ? 'ops' : 'ctg';
    const rangeReady = rangeMode === 'range' && isRangeReady(scenarios);
    const techLabel = TECH_LABELS[techType] || techType.replace(/_/g, ' ');
    const lifecycleLabel = isExisting != null
        ? (isExisting ? LIFECYCLE_LABELS.operating : LIFECYCLE_LABELS.prospective)
        : null;
    const lifetimeYears = lifetimeProjection?.config.projectLifetimeYears;
    const hasLifetime = !!lifetimeProjection;

    const { data: baseline } = useGridBaseline(baCode);
    const gridGenMwh = baseline?.total_generation_mwh;

    const effectiveTotals = mode === 'ops' && opsTotals ? opsTotals : impactTotals;
    const isHealthBurden = effectiveTotals ? effectiveTotals.dalys < 0 : false;
    const isEcoBurden = effectiveTotals ? effectiveTotals.pdfs < 0 : false;
    const isEconBurden = effectiveTotals ? effectiveTotals.usd < 0 : false;
    const isAnyBurden = isHealthBurden || isEcoBurden || isEconBurden;

    // Displacement data
    const displacements = useMemo(() => {
        if (!generationData) return [];
        const totalDisplaced = Object.entries(generationData)
            .filter(([, v]) => (v.Difference ?? 0) < -0.001)
            .reduce((sum, [, v]) => sum + Math.abs(v.Difference ?? 0), 0);
        if (totalDisplaced < 0.001) return [];
        return Object.entries(generationData)
            .filter(([, v]) => (v.Difference ?? 0) < -0.001)
            .map(([fuel, v]) => ({
                fuel,
                label: getFuelLabel(fuel),
                gwh: Math.abs(v.Difference ?? 0),
                pct: (Math.abs(v.Difference ?? 0) / totalDisplaced) * 100,
            }))
            .sort((a, b) => b.gwh - a.gwh);
    }, [generationData]);

    const genGwh = generationMwh ? generationMwh / 1000 : null;
    const totalDisplacedGwh = displacements.reduce((s, d) => s + d.gwh, 0);

    const fmtGwh = (gwh: number) =>
        gwh >= 1 ? `${gwh.toFixed(1)} GWh` : `${(gwh * 1000).toFixed(0)} MWh`;

    const co2Display = avoidedCo2eTonnes != null && avoidedCo2eTonnes > 0
        ? avoidedCo2eTonnes >= 1000
            ? `${(avoidedCo2eTonnes / 1000).toFixed(1)}k`
            : avoidedCo2eTonnes.toFixed(0)
        : null;

    return (
        <div className="bg-surface-700 rounded-lg shadow border border-border overflow-hidden">
            {/* ── Header ── */}
            <div className="px-5 pt-4 pb-3">
                {/* Breadcrumb */}
                {backTo && (
                    <Link
                        to={backTo.to}
                        className="inline-flex items-center gap-1 text-caption text-text-muted hover:text-quantum-400 transition-colors mb-2"
                    >
                        <FiChevronLeft size={12} />
                        {backTo.label}
                    </Link>
                )}

                {/* Identity + actions */}
                <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h2 className="text-lg font-semibold text-text-primary truncate">{projectName}</h2>
                            {lifecycleLabel && (
                                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                                    isExisting
                                        ? 'bg-quantum-500/15 text-quantum-400'
                                        : 'bg-economic-500/15 text-economic-400'
                                }`}>
                                    {lifecycleLabel}
                                </span>
                            )}
                            {onRename && (
                                <button onClick={onRename} className="text-text-muted hover:text-quantum-400 transition-colors" title="Rename">
                                    <FiEdit2 size={13} />
                                </button>
                            )}
                            {onDelete && (
                                <button onClick={onDelete} className="text-text-muted hover:text-health-400 transition-colors" title="Delete">
                                    <FiTrash2 size={13} />
                                </button>
                            )}
                        </div>
                        <p className="text-body-sm text-text-secondary mt-0.5">
                            {techLabel} · {capacity} MW{baCode ? ` · ${baCode}` : ''} · {analysisYear}
                        </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        {lensToggle}
                        {reportButtons}
                        {onEditDetails && (
                            <button onClick={onEditDetails} className="text-text-muted hover:text-quantum-400 transition-colors" title="Edit project details">
                                <FiEdit2 size={15} />
                            </button>
                        )}
                        {onRunModel && (
                            <button
                                onClick={onRunModel}
                                disabled={!canRun}
                                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                                    canRun
                                        ? 'text-quantum-400 border border-quantum-500/40 bg-quantum-500/10 hover:bg-quantum-500/20'
                                        : 'text-text-muted border border-border bg-surface-600 cursor-not-allowed'
                                }`}
                            >
                                <FiPlay size={11} /> Run
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Displacement story (only when results exist) ── */}
            {totalDisplacedGwh > 0.001 && (
                <div className="px-5 pb-4 border-t border-border/50 pt-3">
                    {/* Headline + CO2e */}
                    <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
                        <div>
                            <span className="text-xl font-bold text-quantum-400">{fmtGwh(totalDisplacedGwh)}</span>
                            <span className="ml-1.5 text-body-sm text-text-secondary">fossil fuel displaced</span>
                            {rangeMode === 'range' && (
                                <span
                                    className="ml-2 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium text-text-muted bg-surface-600 border border-border align-middle"
                                    title="Generation is the fixed physical driver — it never gets a sensitivity band; only downstream realization varies."
                                >
                                    <span className="h-1 w-1 rounded-full bg-text-muted" /> fixed input
                                </span>
                            )}
                        </div>
                        {co2Display && (
                            <div>
                                <span className="text-xl font-bold text-economic-400">{co2Display}</span>
                                <span className="ml-1.5 text-body-sm text-text-secondary">t CO2e avoided</span>
                            </div>
                        )}
                    </div>

                    {/* Fuel breakdown bar + per-fuel GWh */}
                    {displacements.length > 0 && (
                        <div className="mt-3">
                            <div className="flex h-2.5 rounded-full overflow-hidden bg-surface-600">
                                {displacements.map((d) => (
                                    <div key={d.fuel} style={{ width: `${d.pct}%`, backgroundColor: getFuelDotColor(d.fuel) }} />
                                ))}
                            </div>
                            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5">
                                {displacements.map((d) => (
                                    <span key={d.fuel} className="flex items-center gap-1.5 text-caption text-text-secondary">
                                        <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: getFuelDotColor(d.fuel) }} />
                                        {d.label} {fmtGwh(d.gwh)}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ── LCA endpoint metrics ── */}
            {effectiveTotals ? (
                <>
                <div className="grid grid-cols-3 gap-0 border-t border-border">
                    {/* Health */}
                    <div className={`border-l-4 p-4 ${isHealthBurden ? 'border-l-amber-400' : 'border-l-health-400'}`}>
                        <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${isHealthBurden ? 'text-amber-400' : 'text-health-400'}`}>
                            Health<InfoTooltip category="health" />
                        </p>
                        <p className={`text-xl font-bold leading-tight ${isHealthBurden ? 'text-amber-400' : 'text-health-400'}`}>
                            {toSigFigs(effectiveTotals.dalys)}
                        </p>
                        <p className="text-[11px] text-text-muted">
                            DALYs {isHealthBurden ? 'net impact' : 'avoided'}{hasLifetime ? ' (Yr 1)' : ''}
                        </p>
                        <BaselineContext
                            annualValue={effectiveTotals.dalys}
                            generationMwh={generationMwh}
                            gridPerMwh={baseline?.human_health_dalys_per_mwh}
                            gridGenMwh={gridGenMwh}
                            metric="dalys"
                        />
                        {rangeReady && (() => {
                            const r = metricRange(scenarios, lens, 'dalys');
                            return r ? (
                                <RangeWhisker floor={r.floor} central={r.central} ceiling={r.ceiling}
                                    benefit={!isHealthBurden} format={(n) => toSigFigs(n)} />
                            ) : null;
                        })()}
                        {hasLifetime && (
                            <p className="text-sm font-semibold text-text-secondary mt-2">
                                {formatImpact(
                                    mode === 'ops' && impactTotals && impactTotals.dalys !== 0
                                        ? lifetimeProjection!.lifetimeAvoidedDalys * (effectiveTotals.dalys / impactTotals.dalys)
                                        : lifetimeProjection!.lifetimeAvoidedDalys
                                , 'dalys')}
                                <span className="font-normal text-text-muted"> ({lifetimeYears}-yr)</span>
                            </p>
                        )}
                    </div>

                    {/* Ecosystem */}
                    <div className={`border-l-4 p-4 ${isEcoBurden ? 'border-l-amber-400' : 'border-l-ecosystem-400'}`}>
                        <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${isEcoBurden ? 'text-amber-400' : 'text-ecosystem-400'}`}>
                            Biodiversity<InfoTooltip category="ecosystem" />
                        </p>
                        <p className={`text-xl font-bold leading-tight ${isEcoBurden ? 'text-amber-400' : 'text-ecosystem-400'}`}>
                            {toSigFigs(effectiveTotals.pdfs)}
                        </p>
                        <p className="text-[11px] text-text-muted">
                            species·yr {isEcoBurden ? 'net impact' : 'protected'}{hasLifetime ? ' (Yr 1)' : ''}
                        </p>
                        <BaselineContext
                            annualValue={effectiveTotals.pdfs}
                            generationMwh={generationMwh}
                            gridPerMwh={baseline?.ecosystem_quality_species_yr_per_mwh}
                            gridGenMwh={gridGenMwh}
                            metric="pdfs"
                        />
                        {rangeReady && (() => {
                            const r = metricRange(scenarios, lens, 'pdfs');
                            return r ? (
                                <RangeWhisker floor={r.floor} central={r.central} ceiling={r.ceiling}
                                    benefit={!isEcoBurden} format={(n) => toSigFigs(n)} />
                            ) : null;
                        })()}
                        {hasLifetime && (
                            <p className="text-sm font-semibold text-text-secondary mt-2">
                                {toSigFigs(
                                    mode === 'ops' && impactTotals && impactTotals.pdfs !== 0
                                        ? lifetimeProjection!.lifetimeAvoidedPdfs * (effectiveTotals.pdfs / impactTotals.pdfs)
                                        : lifetimeProjection!.lifetimeAvoidedPdfs
                                )}
                                <span className="font-normal text-text-muted"> ({lifetimeYears}-yr)</span>
                            </p>
                        )}
                    </div>

                    {/* Economic */}
                    <div className={`border-l-4 p-4 ${isEconBurden ? 'border-l-amber-400' : 'border-l-economic-400'}`}>
                        <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${isEconBurden ? 'text-amber-400' : 'text-economic-400'}`}>
                            Economic<InfoTooltip category="economic" />
                        </p>
                        <p className={`text-xl font-bold leading-tight ${isEconBurden ? 'text-amber-400' : 'text-economic-400'}`}>
                            {formatImpact(effectiveTotals.usd, 'usd')}
                        </p>
                        <p className="text-[11px] text-text-muted">
                            {isEconBurden ? 'net impact' : 'avoided'}{hasLifetime ? ' (Yr 1)' : ''}
                        </p>
                        <BaselineContext
                            annualValue={effectiveTotals.usd}
                            generationMwh={generationMwh}
                            gridPerMwh={baseline?.resource_scarcity_usd_per_mwh}
                            gridGenMwh={gridGenMwh}
                            metric="usd"
                        />
                        {rangeReady && (() => {
                            const r = metricRange(scenarios, lens, 'usd');
                            return r ? (
                                <RangeWhisker floor={r.floor} central={r.central} ceiling={r.ceiling}
                                    benefit={!isEconBurden} format={(n) => formatImpact(n, 'usd')} />
                            ) : null;
                        })()}
                        {hasLifetime && (
                            <p className="text-sm font-semibold text-text-secondary mt-2">
                                {formatImpact(
                                    mode === 'ops' && impactTotals && impactTotals.usd !== 0
                                        ? lifetimeProjection!.lifetimeEconomicNpvUsd * (effectiveTotals.usd / impactTotals.usd)
                                        : lifetimeProjection!.lifetimeEconomicNpvUsd
                                , 'usd')}
                                <span className="font-normal text-text-muted"> ({lifetimeYears}-yr NPV)</span>
                            </p>
                        )}
                    </div>
                </div>
                {isAnyBurden && (
                    <div className="px-5 py-2 bg-amber-500/5 border-t border-amber-500/20">
                        <p className="text-xs text-amber-400/80">
                            Negative values indicate a net burden — this project primarily displaces clean energy sources in its grid region.
                        </p>
                    </div>
                )}
                </>
            ) : (
                <div className="border-t border-border px-5 py-6 text-sm text-text-muted text-center">
                    Run the model to see impact results
                </div>
            )}
        </div>
    );
}
